import type { HTMLAttributes } from "react";
import React from "react";
import type { ColorToken, StyleObject } from "../../theme/styled";
import { styled } from "../../theme/styled";
import type { SpacingToken } from "../../theme/styled/designTokens/spacing/spacingTokens";
import type { Components } from "./baseStyling";
import { font } from "./baseStyling";

type TextAlign = StyleObject["textAlign"];

interface Props extends HTMLAttributes<unknown> {
  // If true, the text will have a bottom margin.
  gutterBottom?: boolean;
  color?: ColorToken | "inherit";
  align?: TextAlign;
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "div" | "span" | "b";
  whiteSpace?: StyleObject["whiteSpace"];
}

type TypographyProps = {
  defaultGutterBottom: boolean;
  gutterBottomSpacing: SpacingToken;
  defaultKivraSansFont?: boolean;
  whiteSpace?: StyleObject["whiteSpace"];
};

export const typographyStyles: Record<Components, TypographyProps> = {
  DisplayL: {
    defaultGutterBottom: true,
    gutterBottomSpacing: "$spacing-24",
    defaultKivraSansFont: true,
  },
  DisplayM: {
    defaultGutterBottom: true,
    gutterBottomSpacing: "$spacing-24",
    defaultKivraSansFont: true,
  },
  HeadingL: {
    defaultGutterBottom: true,
    gutterBottomSpacing: "$spacing-12",
    defaultKivraSansFont: true,
  },
  HeadingM: {
    defaultGutterBottom: true,
    gutterBottomSpacing: "$spacing-12",
    defaultKivraSansFont: true,
  },
  HeadingS: {
    defaultGutterBottom: false,
    gutterBottomSpacing: "$spacing-8",
  },
  HeadingXS: {
    defaultGutterBottom: false,
    gutterBottomSpacing: "$spacing-8",
  },
  BodyM: {
    defaultGutterBottom: false,
    gutterBottomSpacing: "$spacing-24",
    whiteSpace: "pre-line",
  },
  BodyS: {
    defaultGutterBottom: false,
    gutterBottomSpacing: "$spacing-24",
    whiteSpace: "pre-line",
  },
  TitleM: {
    defaultGutterBottom: false,
    gutterBottomSpacing: "$spacing-24",
  },
  TitleS: {
    defaultGutterBottom: false,
    gutterBottomSpacing: "$spacing-24",
  },
  Caption: {
    defaultGutterBottom: false,
    gutterBottomSpacing: "$spacing-12",
  },
  Overline: {
    defaultGutterBottom: false,
    gutterBottomSpacing: "$spacing-12",
  },
};

const generateStyle =
  (component: Components) => (props: Props): StyleObject => {
    const {
      defaultGutterBottom,
      gutterBottomSpacing,
      defaultKivraSansFont,
      whiteSpace: defaultWhiteSpace,
    } = typographyStyles[component];
    return {
      ...font[component],
      fontFamily: defaultKivraSansFont ? "$heading-font" : "$base-font",
      color: props.color || "$text-primary",
      margin: (props.gutterBottom ?? defaultGutterBottom)
        ? [0, 0, gutterBottomSpacing, 0]
        : 0,
      padding: 0,
      textAlign: props.align,
      whiteSpace: props.whiteSpace ?? defaultWhiteSpace,
    };
  };

export const Display = ({
  size,
  ...rest
}: Props & { size: "large" | "medium" }): React.JSX.Element => {
  if (size === "medium") {
    return <DisplayM {...rest} />;
  } else {
    return <DisplayL {...rest} />;
  }
};

export const Heading = ({
  size,
  ...rest
}: Props & {
  size: "large" | "medium" | "small" | "xsmall";
}): React.JSX.Element => {
  switch (size) {
    case "xsmall":
      return <HeadingXS {...rest} />;
    case "small":
      return <HeadingS {...rest} />;
    case "medium":
      return <HeadingM {...rest} />;
    case "large":
      return <HeadingL {...rest} />;
  }
};

export const Body = ({
  size,
  ...rest
}: Props & { size: "small" | "medium" }): React.JSX.Element => {
  if (size === "small") {
    return <BodyS {...rest} />;
  } else {
    return <BodyM {...rest} />;
  }
};

export const Title = ({
  size,
  ...rest
}: Props & { size: "small" | "medium" }): React.JSX.Element => {
  if (size === "small") {
    return <TitleS {...rest} />;
  } else {
    return <TitleM {...rest} />;
  }
};

const DisplayL = styled.h1(generateStyle("DisplayL"));
const DisplayM = styled.h1(generateStyle("DisplayM"));
const HeadingL = styled.h1(generateStyle("HeadingL"));
const HeadingM = styled.h2(generateStyle("HeadingM"));
const HeadingS = styled.h3(generateStyle("HeadingS"));
const HeadingXS = styled.h4(generateStyle("HeadingXS"));
const BodyM = styled.p(generateStyle("BodyM"));
const BodyS = styled.p(generateStyle("BodyS"));
const TitleM = styled.p(generateStyle("TitleM"));
const TitleS = styled.p(generateStyle("TitleS"));
export const Caption = styled.p<Props>(generateStyle("Caption"));
export const Overline = styled.p<Props>(generateStyle("Overline"));
